import { HTMLProps } from 'react';
import styled, { css } from 'styled-components';
import { shouldIgnoreProp } from '../../utils/shouldIgnoreProp';
import { Flex, FlexProps } from '../layout/Flex';

export type IconButtonSize = 'xxs' | 'xs' | 's' | 'm' | 'l' | 'xl';

interface IconButtonProps
    extends FlexProps,
        Omit<HTMLProps<HTMLButtonElement>, 'color' | 'size' | 'width' | 'height'> {
    size?: IconButtonSize;
    variant?: 'flat' | 'filled' | 'fab';
}

export const IconButton = styled(Flex)
    .withConfig({
        displayName: 'IconButton',
        shouldForwardProp: shouldIgnoreProp('size', 'variant', 'shape'),
    })
    .attrs<IconButtonProps>((props) => ({
        as: 'button',
        alignItems: 'center',
        justifyContent: 'center',
        type: 'button',
        variant: 'flat',
        borderRadius: props.shape === 'rect' ? 1 : '50%',
        cursor: props.cursor || 'pointer',
        position: props.position || 'relative',
        ...props,
        size: props.size || 'm',
    }))<IconButtonProps>`
    display: inline-flex;
    -webkit-tap-highlight-color: transparent;
    background-color: transparent;
    outline: 0;
    border: 0;
    margin: 0;
    user-select: none;
    appearance: none;
    text-decoration: none;
    text-align: center;
    font-family: inherit;
    overflow: hidden;
    flex: 0 0 auto;
    color: inherit;
    transition-duration: 150ms;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    transition-delay: 0ms;
    transition-property: color, transform;
    will-change: transform, opacity;

    ${(props) =>
        props.variant === 'flat' &&
        css`
            color: var(--color-primary);
        `}

    ${(props) =>
        props.variant === 'filled' &&
        css`
            color: var(--control-secondary-blur-color);
            background-color: var(--control-secondary-blur-background);
            -webkit-backdrop-filter: blur(20px);
            backdrop-filter: blur(20px);
        `}

    ${(props) =>
        props.variant === 'fab' &&
        css`
            box-shadow: 0 8px 20px 0 #0000001f;
            color: var(--color-primary);
            background-color: var(--background-primary);
        `}

    @media screen and (pointer: fine) {
        &:hover {
            transform: scale(1.1);
        }
    }

    &:active {
        transform: scale(0.9);
    }

    &:disabled,
    &[disabled] {
        transform: scale(1);
    }

    ${(props) =>
        props.size === 'xxs' &&
        css`
            font-size: 14px;
            padding: ${props.padding ?? '4px'};
        `}

    ${(props) =>
        props.size === 'xs' &&
        css`
            font-size: 18px;
            padding: ${props.padding ?? '4px'};
        `}

    ${(props) =>
        props.size === 's' &&
        css`
            font-size: 20px;
            padding: ${props.padding ?? '4px'};
        `}

    ${(props) =>
        props.size === 'm' &&
        css`
            font-size: 24px;
            padding: ${props.padding ?? '8px'};
        `}

    ${(props) =>
        props.size === 'l' &&
        css`
            font-size: 24px;
            padding: ${props.padding ?? '12px'};
        `}

    ${(props) =>
        props.size === 'xl' &&
        css`
            font-size: 28px;
            padding: ${props.padding ?? '14px'};
        `}
    ${(props) =>
        props.backgroundColor &&
        css`
            background-color: ${props.backgroundColor};
        `}
        
    ${(props) =>
        props.color &&
        css`
            color: ${props.color};
        `}
`;
